/*
 * Staff Waiter-Call API client.
 *
 * Authenticated endpoints used by the POS dashboard and the per-waiter
 * mobile app. Refer to docs/QR_WAITER_CALL.md (sections 2 & 5) for the
 * full backend contract — the types below mirror the response shapes
 * 1-to-1.
 *
 * Endpoints (sanctum bearer auth + has.restaurant gate):
 *   GET    /api/waiter-calls                  — every open call in the restaurant
 *   GET    /api/waiter-calls/mine             — calls assigned to me + unassigned
 *   PATCH  /api/waiter-calls/{id}/acknowledge — "heard you, on my way"
 *   PATCH  /api/waiter-calls/{id}/resolve     — "done, close it"
 *
 * Channel reference (paired hook subscribes to these):
 *   restaurant.{rid}.waiter-calls                          → POS dashboard
 *   restaurant.{rid}.staff.{userId}.waiter-calls           → personal waiter
 */

import axios from 'axios';
import { base_url } from './index';
import {
  QrWaiterCallStatus,
  QrWaiterAssignedStaff,
  QrWaiterCalledEvent,
  QrWaiterCallStatusChangedEvent,
} from './qrWaiterCall';

// ---------- Re-exports so widget code only imports from one module ----------
export type {
  QrWaiterCallStatus,
  QrWaiterAssignedStaff,
  QrWaiterCalledEvent,
  QrWaiterCallStatusChangedEvent,
};

// ---------- Domain types ----------

/** Backend representation of a waiter call as exposed to the staff. */
export interface StaffWaiterCall {
  call_id: number;
  table: { id: number; name: string };
  status: QrWaiterCallStatus;
  reason_code: string | null;
  reason_label: string | null;
  note: string | null;
  /** Waiter the table is assigned to (last order taker). May be null. */
  assigned_waiter: QrWaiterAssignedStaff | null;
  /** Whoever first hit acknowledge — populated only after that step. */
  acknowledged_by: QrWaiterAssignedStaff | null;
  created_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
}

/** Wrapped list response — both endpoints return `{ calls: [...] }`. */
export interface StaffWaiterCallListResponse {
  calls: StaffWaiterCall[];
}

/** Acknowledge / resolve responses are idempotent — backend echoes the row. */
export interface StaffWaiterCallActionResponse {
  call: StaffWaiterCall;
  message?: string;
}

// ---------- HTTP client ----------

const staffClient = axios.create({
  baseURL: base_url,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 15000,
});

// Inject the bearer token on every request — sourced from the same place as
// the rest of the app so logout/staff-switch automatically propagate.
staffClient.interceptors.request.use((config) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  if (token && config.headers) {
    // Axios v1 ships AxiosHeaders with .set; fall back to bracket-set for
    // older v0 builds. Either way avoid replacing the whole headers object
    // because that breaks the AxiosRequestHeaders type contract.
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------- API calls ----------

/**
 * POS dashboard — every open call in the restaurant. Backend orders the list
 * with `pending` first, then `acknowledged`, both ascending by created_at so
 * the oldest open call is at the top.
 */
export async function getAllWaiterCalls(
  signal?: AbortSignal,
): Promise<StaffWaiterCallListResponse> {
  const res = await staffClient.get<StaffWaiterCallListResponse>('/waiter-calls', { signal });
  return res.data;
}

/**
 * Per-waiter mobile app — calls already assigned to the current user PLUS
 * unassigned calls (so the first idle waiter can pick them up).
 */
export async function getMineWaiterCalls(
  signal?: AbortSignal,
): Promise<StaffWaiterCallListResponse> {
  const res = await staffClient.get<StaffWaiterCallListResponse>('/waiter-calls/mine', {
    signal,
  });
  return res.data;
}

/**
 * Acknowledge a call. Idempotent — calling twice returns the same payload
 * with the same `acknowledged_by`/`acknowledged_at` (the second caller does
 * not overwrite the first).
 */
export async function acknowledgeWaiterCall(
  callId: number,
  signal?: AbortSignal,
): Promise<StaffWaiterCallActionResponse> {
  const res = await staffClient.patch<StaffWaiterCallActionResponse>(
    `/waiter-calls/${callId}/acknowledge`,
    {},
    { signal },
  );
  return res.data;
}

/**
 * Resolve (close) a call. If `acknowledged_by` was still null the backend
 * fills it in with the resolving user.
 */
export async function resolveWaiterCall(
  callId: number,
  signal?: AbortSignal,
): Promise<StaffWaiterCallActionResponse> {
  const res = await staffClient.patch<StaffWaiterCallActionResponse>(
    `/waiter-calls/${callId}/resolve`,
    {},
    { signal },
  );
  return res.data;
}
